import { environment } from '../../../environments/environment';
import { Product } from '../../product/product.types';
import { htmlToPlainText } from '../common/utils';

/**
 * Plain-text description for SEO tags, trying each catalog field in order.
 * Rich-text editors save "empty" content as markup like `<p><br></p>`, so a
 * field only counts if it still has text AFTER stripping tags/entities
 * (ELIO-6717: the truthy-before-strip check emitted `"description":""`).
 */
export function buildSeoDescription(product: Product): string {
  const candidates = [
    product.description?.short,
    (product as any).teaserDescription,
    product.description?.long
  ];
  for (const candidate of candidates) {
    const text = htmlToPlainText(candidate);
    if (text) return text;
  }
  return '';
}

/**
 * Builds the Product JSON-LD (schema.org) object for a product detail page.
 *
 * Shared by the SSR route resolver (`product.resolver.ts`) and the client
 * component (`product.component.ts`) so both emit byte-identical structured
 * data and can never drift apart.
 *
 * `absoluteImageUrl` is passed in already resolved because absolute-URL
 * resolution goes through CanonicalService and differs between the SSR and
 * browser contexts.
 *
 * The `hasMerchantReturnPolicy` block is emitted only when
 * `environment.config.seo.returnPolicy` is configured for the brand, keeping
 * this white-label safe (branches without a policy simply omit it). Shipping
 * (`shippingDetails`) is intentionally NOT declared — see ELIO-6488.
 */
export function buildProductJsonLd(product: Product, absoluteImageUrl: string | null): any {
  const appName = environment.application.name;

  const data: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: String(product.id),
    brand: { '@type': 'Brand', name: product.producer?.name || appName },
    offers: {
      '@type': 'Offer',
      price: String(product.price ?? ''),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: appName }
    }
  };

  const rp = (environment.config.seo as any)?.returnPolicy;
  if (rp) {
    const policy: any = {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: rp.applicableCountry,
      returnPolicyCategory: rp.returnPolicyCategory
    };
    if (rp.merchantReturnDays != null) policy.merchantReturnDays = rp.merchantReturnDays;
    if (rp.returnMethod) policy.returnMethod = rp.returnMethod;
    if (rp.returnFees) policy.returnFees = rp.returnFees;
    if (rp.refundType) policy.refundType = rp.refundType;
    data.offers.hasMerchantReturnPolicy = policy;
  }

  const description = buildSeoDescription(product);
  if (description) data.description = description.substring(0, 5000);
  if (absoluteImageUrl) data.image = absoluteImageUrl;
  if (product.category?.name) data.category = product.category.name;

  return data;
}
